import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';

export default function CaseRecuperacaoCorrecaoEstruturalVentiladorTerciarioPage() {
  useSeo({
    title: 'Recuperação e Correção Estrutural de Ventilador Terciário | Case Pilar',
    description: 'Demonstração dos serviços de recuperação estrutural e correção de bases de acionamento de ventilador terciário industrial, com fabricação de novas bases, correções de alinhamento e reposicionamento do conjunto motor-redutor.',
    keywords: 'ventilador terciário, recuperação estrutural, correção de bases, motor-redutor, manutenção industrial, ventilador industrial, engenharia de manutenção, papel e celulose, Grupo Pilar, Aracruz, ES',
    canonical: 'https://www.pilarmanutencao.com.br/cases/recuperacao-correcao-estrutural-ventilador-terciario',
    ogUrl: 'https://www.pilarmanutencao.com.br/cases/recuperacao-correcao-estrutural-ventilador-terciario',
    ogImage: 'https://readdy.ai/api/search-image?query=Extremely%20realistic%20professional%20industrial%20photography%20modern%20clean%20organized%20heavy%20industrial%20workshop%20with%20large%20machinery%20hydraulic%20equipment%20steel%20components%20metallic%20surfaces%20professional%20technicians%20in%20safety%20gear%20working%20not%20facing%20camera%20cinematic%20dramatic%20lighting%20gray%20silver%20steel%20tones%20with%20subtle%20warm%20yellow%20accent%20details%20wide%20angle%20widescreen%20composition%20premium%20high%20resolution%20sharp%20depth%20industrial%20maintenance%20engineering%20reliability%20heavy%20mechanics%20pulp%20paper%20mining%20oil%20gas%20sectors%20negative%20space%20for%20title%20overlay%20clean%20backgrounds%20not%20stock%20photo%20not%20illustration%20not%203D%20render&width=1600&height=700&seq=gp-cases-unified-hero-v2&orientation=landscape',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroImage = 'https://readdy.ai/api/search-image?query=Extremely%20realistic%20professional%20industrial%20photography%20modern%20clean%20organized%20heavy%20industrial%20workshop%20with%20large%20machinery%20hydraulic%20equipment%20steel%20components%20metallic%20surfaces%20professional%20technicians%20in%20safety%20gear%20working%20not%20facing%20camera%20cinematic%20dramatic%20lighting%20gray%20silver%20steel%20tones%20with%20subtle%20warm%20yellow%20accent%20details%20wide%20angle%20widescreen%20composition%20premium%20high%20resolution%20sharp%20depth%20industrial%20maintenance%20engineering%20reliability%20heavy%20mechanics%20pulp%20paper%20mining%20oil%20gas%20sectors%20negative%20space%20for%20title%20overlay%20clean%20backgrounds%20not%20stock%20photo%20not%20illustration%20not%203D%20render&width=1600&height=700&seq=gp-cases-unified-hero-v2&orientation=landscape';

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt="Ventilador terciário após conclusão dos ajustes estruturais e posicionamento final do conjunto de acionamento"
              title="Ventilador terciário após conclusão dos ajustes estruturais e posicionamento final do conjunto de acionamento"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="inline-block px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wider bg-gp-yellow text-gp-graphite">
                  Oficina Especializada
                </span>
                <span className="text-gray-400 text-xs uppercase tracking-wider">Papel e Celulose</span>
                <span className="text-gray-500 text-xs">— Aracruz, ES</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Case:</span>{' '}
                Recuperação e Correção Estrutural de Ventilador Terciário Industrial
              </h1>
              <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
                Execução de serviços de recuperação estrutural e correção de bases de acionamento de
                ventilador terciário industrial, incluindo fabricação de novas bases, correções de
                alinhamento, adequação estrutural e reposicionamento do conjunto motor-redutor.
              </p>
            </div>
          </div>
        </section>

        {/* Visão Geral */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Visão Geral</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-information-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Dados do Projeto</h3>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-settings-3-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Equipamento</span>
                        <p className="text-gray-300 text-sm">Ventilador Terciário Industrial com Conjunto Motor-Redutor</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-building-2-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Segmento</span>
                        <p className="text-gray-300 text-sm">Papel e Celulose</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-tools-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Aplicação</span>
                        <p className="text-gray-300 text-sm">Sistema de acionamento rotativo de ventilador terciário em processo industrial</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-brain-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Abordagem</span>
                        <p className="text-gray-300 text-sm">Engenharia aplicada com correção estrutural, fabricação de bases e reposicionamento de conjunto motriz</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-bar-chart-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Diferencial do Projeto</span>
                        <p className="text-gray-300 text-sm">Fabricação de bases novas e reposicionamento do conjunto motor-redutor com correção estrutural completa</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/1132bb4d-eb03-45c3-b2a0-916d96f46259_compressed_POSICIONAMENTO-MOTOR.webp"
                    alt="Reposicionamento do conjunto motriz durante execução dos serviços de recuperação estrutural"
                    title="Reposicionamento do conjunto motriz durante execução dos serviços de recuperação estrutural"
                    className="w-full h-auto object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Reposicionamento do conjunto motriz durante execução dos serviços de recuperação estrutural.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Desafio */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Desafio</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-alert-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-gray-300 text-base leading-relaxed mb-4">
                        O ventilador terciário apresentava desvios estruturais significativos nas bases de
                        apoio do conjunto de acionamento, comprometendo o alinhamento e a confiabilidade
                        operacional do sistema. As bases existentes sofriam desgaste, oxidação e deformação
                        ao longo do tempo, afetando o correto posicionamento do motor-redutor e
                        transmitindo vibrações excessivas para o conjunto rotativo.
                      </p>
                      <p className="text-gray-400 text-sm leading-relaxed mb-4">
                        As principais complexidades envolviam:
                      </p>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Desvios estruturais nas bases de apoio com deformação e desgaste progressivo</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Comprometimento do alinhamento entre motor, redutor e ventilador</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Transmissão de vibrações excessivas para o conjunto rotativo devido às bases deterioradas</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Oxidação e corrosão avançada nas bases de apoio do conjunto motor-redutor</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Necessidade de intervenção corretiva sem substituição completa do conjunto</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Alta criticidade para o processo produtivo com impacto na operação industrial</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/777ad1e2-7d4a-418b-b3e9-cbcc4649cd87_compressed_BASES-PARA-CORRECAO.webp"
                    alt="Identificação de desvios estruturais e necessidade de correção das bases de apoio do conjunto"
                    title="Identificação de desvios estruturais e necessidade de correção das bases de apoio do conjunto"
                    className="w-full h-auto object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Identificação de desvios estruturais e necessidade de correção das bases de apoio do conjunto.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Engenharia Aplicada */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Engenharia Aplicada</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start mb-8">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-search-eye-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Metodologia Aplicada</h3>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    A Pilar aplicou metodologia de engenharia para reestruturar o sistema de apoio do
                    ventilador terciário. A abordagem envolveu análise dimensional completa, fabricação
                    de novas bases sob medida, adequação estrutural e reposicionamento do conjunto
                    motor-redutor com garantia de alinhamento preciso.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Análise dimensional completa das bases de apoio existentes e do conjunto motor-redutor</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Levantamento das deformações, desvios de planicidade e condições de oxidação das bases</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Projeto de fabricação de novas bases com compatibilização dimensional do conjunto</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Definição de materiais adequados e especificações de usinagem e tratamento superficial</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Planejamento do reposicionamento do conjunto motor-redutor com sequência de intervenção</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Preparação de ferramentas, dispositivos e equipe para execução da recuperação estrutural</span>
                    </li>
                  </ul>
                </div>

                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/76cef16d-3ec5-4876-b8bd-131618f49497_compressed_NOVA-BASE-MOTOR-VENTILADOR.webp"
                    alt="Fabricação e instalação de nova base para adequação estrutural do sistema de acionamento"
                    title="Fabricação e instalação de nova base para adequação estrutural do sistema de acionamento"
                    className="w-full h-72 md:h-80 object-contain p-4 bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Fabricação e instalação de nova base para adequação estrutural do sistema de acionamento.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Correção Estrutural */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Correção Estrutural</h2>
              </div>

              <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter mb-8">
                <img
                  src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/602ee8a5-a08c-4fc3-9d7d-1ad7cd69b104_compressed_ACERTO-BASE-MOTOR-REDUTOR.webp"
                  alt="Correções dimensionais e ajustes de alinhamento realizados na base do motor-redutor"
                  title="Correções dimensionais e ajustes de alinhamento realizados na base do motor-redutor"
                  className="w-full h-auto object-contain p-4 bg-gp-graphite"
                  loading="lazy"
                />
                <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                  <p className="text-gray-400 text-xs text-center">
                    Correções dimensionais e ajustes de alinhamento realizados na base do motor-redutor.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">01</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Desmontagem do conjunto motor-redutor com liberação segura do sistema de acionamento</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">02</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Remoção das bases de apoio deterioradas com avaliação estrutural da fundação</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">03</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Limpeza e preparação da superfície de apoio para recebimento das novas bases</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">04</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Fabricação de novas bases conforme levantamento dimensional e especificação de engenharia</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">05</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Instalação das novas bases com verificação de planicidade e nivelamento</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">06</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Ajustes de planicidade e alinhamento com instrumentação de precisão</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">07</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Reposicionamento do conjunto motor-redutor sobre as novas bases de apoio</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">08</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Alinhamento mecânico final entre motor, redutor e ventilador com instrumentação de precisão</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">09</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Fixação estrutural com parafusos de alta resistência e aperto conforme especificação técnica</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">10</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Verificação de vibração e inspeções finais para liberação operacional</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Resultado Final */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Resultado Final</h2>
              </div>

              <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8 mb-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Conjunto Motor-Redutor Reposicionado e Alinhado</h3>
                  </div>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Após a conclusão das intervenções estruturais, o conjunto motor-redutor foi
                  reposicionado e alinhado com precisão sobre as novas bases de apoio. As correções
                  dimensionais eliminaram os desvios estruturais existentes, proporcionando estabilidade
                  mecânica ao sistema de acionamento do ventilador terciário. A nova configuração
                  reduziu a transmissão de vibrações, aumentou a confiabilidade operacional e ampliou a
                  vida útil dos componentes do conjunto rotativo.
                </p>
              </div>

              <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                <img
                  src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/5881d9ac-00c4-4ab8-b8f2-643cc09fef68_compressed_POSICIONAMENTO-FINAL-MOTOR.webp"
                  alt="Conjunto motor-redutor reposicionado e alinhado após conclusão das intervenções estruturais"
                  title="Conjunto motor-redutor reposicionado e alinhado após conclusão das intervenções estruturais"
                  className="w-full h-auto object-contain p-4 bg-gp-graphite"
                  loading="lazy"
                />
                <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                  <p className="text-gray-400 text-xs text-center">
                    Conjunto motor-redutor reposicionado e alinhado após conclusão das intervenções estruturais.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Galeria Técnica */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Galeria Técnica</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Foto 1 */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/1132bb4d-eb03-45c3-b2a0-916d96f46259_compressed_POSICIONAMENTO-MOTOR.webp"
                      alt="Reposicionamento do conjunto motriz durante execução dos serviços de recuperação estrutural"
                      title="Reposicionamento do conjunto motriz durante execução dos serviços de recuperação estrutural"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 1</span>
                  </div>
                </div>
                {/* Foto 2 */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/5881d9ac-00c4-4ab8-b8f2-643cc09fef68_compressed_POSICIONAMENTO-FINAL-MOTOR.webp"
                      alt="Ventilador terciário após conclusão dos ajustes estruturais e posicionamento final do conjunto de acionamento"
                      title="Ventilador terciário após conclusão dos ajustes estruturais e posicionamento final do conjunto de acionamento"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 2</span>
                  </div>
                </div>
                {/* Foto 3 */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/777ad1e2-7d4a-418b-b3e9-cbcc4649cd87_compressed_BASES-PARA-CORRECAO.webp"
                      alt="Identificação de desvios estruturais e necessidade de correção das bases de apoio do conjunto"
                      title="Identificação de desvios estruturais e necessidade de correção das bases de apoio do conjunto"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 3</span>
                  </div>
                </div>
                {/* Foto 4 */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/76cef16d-3ec5-4876-b8bd-131618f49497_compressed_NOVA-BASE-MOTOR-VENTILADOR.webp"
                      alt="Fabricação e instalação de nova base para adequação estrutural do sistema de acionamento"
                      title="Fabricação e instalação de nova base para adequação estrutural do sistema de acionamento"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 4</span>
                  </div>
                </div>
                {/* Foto 5 */}
                <div className="relative rounded-lg border border-gp-graphite-lighter bg-gp-graphite">
                  <div className="h-64 md:h-72 flex items-center justify-center">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/602ee8a5-a08c-4fc3-9d7d-1ad7cd69b104_compressed_ACERTO-BASE-MOTOR-REDUTOR.webp"
                      alt="Correções dimensionais e ajustes de alinhamento realizados na base do motor-redutor"
                      title="Correções dimensionais e ajustes de alinhamento realizados na base do motor-redutor"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 5</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Serviços Relacionados */}
        <section className="relative py-16 overflow-hidden border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-lg font-bold text-white uppercase tracking-wider">Serviços Relacionados</h2>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/mecanica-industrial"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Mecânica Industrial
                </Link>
                <Link
                  to="/caldeiraria-solda"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Caldeiraria e Solda
                </Link>
                <Link
                  to="/hidraulica-industrial"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Hidráulica Industrial
                </Link>
                <Link
                  to="/oficina"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Oficina Especializada
                </Link>
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Mais Cases de Sucesso
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative py-20 bg-gp-graphite-light overflow-hidden">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Seu ventilador industrial apresenta desvios estruturais e bases deterioradas?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Conte com a Pilar para recuperar estruturalmente os sistemas de acionamento de seus
                  ventiladores industriais. Fabricamos bases novas sob medida, corrigimos alinhamentos e
                  reposicionamos conjuntos motriz com precisão de engenharia.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="https://wa.me/5527993092922"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                  >
                    <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <span>Solicitar Avaliação Estrutural</span>
                    <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                  </a>
                  <Link
                    to="/contato"
                    className="inline-flex items-center justify-center gap-2 border border-gray-500 hover:border-gp-yellow text-gray-300 hover:text-gp-yellow px-8 py-4 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-mail-line text-lg w-6 h-6 flex items-center justify-center"></i>
                    Falar com Engenheiro
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Cases */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/cases"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Cases
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />

      {/* Schema JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Recuperação e Correção Estrutural de Ventilador Terciário Industrial | Case Grupo Pilar',
            description: 'Demonstração dos serviços de recuperação estrutural e correção de bases de acionamento de ventilador terciário industrial, com fabricação de novas bases, correções de alinhamento e reposicionamento do conjunto motor-redutor.',
            image: heroImage,
            author: {
              '@type': 'Organization',
              name: 'Grupo Pilar',
              url: 'https://www.pilarmanutencao.com.br',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Grupo Pilar',
              logo: {
                '@type': 'ImageObject',
                url: '/images/MARCA/LOGO-GRUPO-PILAR/LOGO-GRUPO-PILAR.png',
              },
            },
            datePublished: '2026-06-23',
            dateModified: '2026-06-23',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://www.pilarmanutencao.com.br/cases/recuperacao-correcao-estrutural-ventilador-terciario',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: 'https://www.pilarmanutencao.com.br',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Cases de Sucesso',
                item: 'https://www.pilarmanutencao.com.br/cases',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: 'Recuperação e Correção Estrutural de Ventilador Terciário Industrial',
                item: 'https://www.pilarmanutencao.com.br/cases/recuperacao-correcao-estrutural-ventilador-terciario',
              },
            ],
          }),
        }}
      />
    </div>
  );
}